import { Avatar, AvatarGroup, Box, Card, CardActionArea, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ChevronRight, Groups, Handshake, PauseCircleOutline, ReportProblem, Savings, Schedule, Verified } from "@mui/icons-material";
import { FlagIcon } from "react-flag-kit";
import { countryToCode } from "../../utils/country_code_mapping";
import { ProjectListItem } from "../../types/projectTypes";
import ProjectProgress, { HEALTH_STYLES, healthOf } from "./ProjectProgress";
import { conditionLabel, formatMoney, formatPercent, initials, panelSx, projectHeadline, splitList } from "./projectUtils";

type ProjectCardProps = {
    item: ProjectListItem;
    onDetailsClick: (id: number) => void;
};

// Planned margin tag on top of the card - hidden for now, the margin calculation is still being polished.
const SHOW_PLANNED_MARGIN = false;

type Pill = { label: string; color: string; Icon: React.ElementType };

// Planned margin colour: >= 25 % good, >= 10 % amber, below red (with icon + text, never colour alone).
const marginTone = (pct: number) => (pct >= 0.25 ? '#0ca30c' : pct >= 0.1 ? '#BA7517' : '#d03b3b');

// One status pill per card, most important first: a condition that is not normal, then the
// invoicing health of ongoing projects, then the plain status.
const pillFor = (item: ProjectListItem): Pill | null => {
    const condition = conditionLabel(item.project_condition);
    if (condition) {
        const serious = item.project_condition === 'Escalated';
        const paused = item.project_condition === 'Dormant / Sleeping';
        return { label: condition, color: serious ? '#d03b3b' : paused ? '#888780' : '#BA7517', Icon: serious ? ReportProblem : paused ? PauseCircleOutline : Schedule };
    }
    if (item.status === 'Ongoing') {
        const health = healthOf(item.completion_ratio, item.time_elapsed_ratio);
        if (health) {
            const style = HEALTH_STYLES[health];
            return { label: health === 'good' ? 'On track' : health === 'behind' ? 'Behind' : 'Ahead', color: style.color, Icon: style.Icon };
        }
        return null;
    }
    if (item.status === 'Yet to start') return { label: 'Yet to start', color: '#378ADD', Icon: Schedule };
    if (item.status === 'On hold') return { label: 'On hold', color: '#888780', Icon: PauseCircleOutline };
    if (item.status === 'Closed') return { label: 'Closed', color: '#5F5E5A', Icon: Verified };
    return null;
};

function CountryFlags({ countries }: { countries: string[] }) {
    if (!countries.length) return null;
    const shown = countries.slice(0, 2);
    const hidden = countries.length - shown.length;
    return (
        <Tooltip title={countries.join(', ')}>
            <Stack direction="row" alignItems="center" gap={0.5} component="span" sx={{ minWidth: 0 }}>
                {shown.map((country) => (
                    <Stack key={country} direction="row" alignItems="center" gap={0.5} component="span">
                        {countryToCode[country] && (
                            <Box component="span" sx={{ display: 'inline-flex', borderRadius: '2px', overflow: 'hidden', boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)' }}>
                                <FlagIcon code={countryToCode[country]} size={16} />
                            </Box>
                        )}
                        <span>{country}</span>
                    </Stack>
                ))}
                {hidden > 0 && <span>+{hidden}</span>}
            </Stack>
        </Tooltip>
    );
}

function ProjectCard({ item, onDetailsClick }: ProjectCardProps) {
    const theme = useTheme();
    const countries = splitList(item.countries);
    const partners = splitList(item.partners);
    const directors = splitList(item.project_directors);
    const managers = splitList(item.project_managers);
    const people = [...directors.map((name) => ({ name, role: 'Project Director' })), ...managers.map((name) => ({ name, role: 'Project Manager' }))];
    const pill = pillFor(item);
    const headline = projectHeadline(item);

    return (
        <Card elevation={0} sx={{
            ...panelSx(theme),
            height: '100%',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
            '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
                boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.10)}`,
                transform: 'translateY(-1px)',
            },
        }}>
            <CardActionArea onClick={() => onDetailsClick(item.ID)} sx={{ height: '100%', padding: 2.25, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                {SHOW_PLANNED_MARGIN && item.planned_margin_pct !== null && item.planned_margin_pct !== undefined && (
                    <Tooltip title={`Planned net profit ${formatMoney(item.planned_net_profit, 'EUR')} (internal margin calculation)`}>
                        <Stack direction="row" alignItems="center" gap={0.75} sx={{
                            marginBottom: 1.25, paddingX: 1.25, paddingY: 0.5, borderRadius: 1.5, alignSelf: 'flex-start',
                            backgroundColor: alpha(marginTone(item.planned_margin_pct), 0.12),
                        }}>
                            <Savings sx={{ fontSize: 16, color: marginTone(item.planned_margin_pct) }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Planned margin {formatPercent(item.planned_margin_pct, 1)}</Typography>
                            <Typography variant="body2" color="text.secondary">· {formatMoney(item.planned_net_profit, 'EUR')}</Typography>
                        </Stack>
                    </Tooltip>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ color: 'text.secondary', fontSize: 13 }}>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{item.project_number}</Typography>
                            <span>·</span>
                            <span>{item.client || 'No client'}</span>
                            {countries.length > 0 && <span>·</span>}
                            <CountryFlags countries={countries} />
                        </Stack>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, marginTop: 0.5 }}>
                            {item.project_name}
                        </Typography>
                    </Box>
                    {pill && (
                        <Chip size="small" icon={<pill.Icon sx={{ fontSize: 16, color: `${pill.color} !important` }} />} label={pill.label}
                            sx={{ backgroundColor: alpha(pill.color, 0.12), color: theme.palette.text.primary, fontWeight: 500, flexShrink: 0 }} />
                    )}
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1.5, lineHeight: 1.6 }}>
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{headline.lead}</Box>{headline.rest}
                </Typography>

                {(item.status === 'Ongoing' || item.status === 'On hold') && (
                    <Box sx={{ marginTop: 1.5 }}>
                        <ProjectProgress item={item} variant="bar" />
                    </Box>
                )}

                {partners.length > 0 && (
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{ marginTop: 1.5, color: 'text.secondary', minWidth: 0 }}>
                        {item.lead ? <Groups sx={{ fontSize: 18 }} /> : <Handshake sx={{ fontSize: 18 }} />}
                        <Typography variant="body2" noWrap title={partners.join(', ')}>
                            {item.lead ? 'INT lead with ' : 'INT as partner with '}
                            <Box component="span" sx={{ color: 'text.primary' }}>{partners.join(', ')}</Box>
                        </Typography>
                    </Stack>
                )}

                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: 2 }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        {people.length > 0 ? (
                            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 11, borderWidth: 1.5 } }}>
                                {people.map((person, index) => (
                                    <Tooltip key={`${person.role}-${person.name}`} title={`${person.name} · ${person.role}`}>
                                        <Avatar sx={index === 0
                                            ? { backgroundColor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }
                                            : { backgroundColor: theme.palette.action.hover, color: theme.palette.text.secondary }}>
                                            {initials(person.name)}
                                        </Avatar>
                                    </Tooltip>
                                ))}
                            </AvatarGroup>
                        ) : (
                            <Typography variant="caption" color="text.secondary">No PD / PM assigned</Typography>
                        )}
                        {item.reference_ID && (
                            <Tooltip title="This project is a reference">
                                <Verified sx={{ fontSize: 18, color: 'secondary.main' }} />
                            </Tooltip>
                        )}
                    </Stack>
                    <Stack direction="row" alignItems="center" sx={{ color: 'primary.main' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Details</Typography>
                        <ChevronRight fontSize="small" />
                    </Stack>
                </Stack>
            </CardActionArea>
        </Card>
    );
}

export default ProjectCard;
